import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type {
  IndividualEntry,
  CreateIndividualEntryRequest,
  AgeSubcategory,
  Gender,
} from '../../types/individualEntry';
import {
  AGE_SUBCATEGORY_LABELS,
  AGE_SUBCATEGORY_ORDER,
} from '../../types/individualEntry';

interface Props {
  open: boolean;
  entry: IndividualEntry | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (req: CreateIndividualEntryRequest) => void;
}

export function IndividualEntryDialog({ open, entry, loading, onCancel, onSubmit }: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthYear, setBirthYear] = useState<string>('');
  const [gender, setGender] = useState<Gender>('M');
  const [ageSubcategory, setAgeSubcategory] = useState<AgeSubcategory>('OSTATNI');
  const [sdhUnitName, setSdhUnitName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [startNumber, setStartNumber] = useState<string>('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      if (entry) {
        setFirstName(entry.firstName);
        setLastName(entry.lastName);
        setBirthYear(entry.birthYear?.toString() ?? '');
        setGender(entry.gender);
        setAgeSubcategory(entry.ageSubcategory);
        setSdhUnitName(entry.sdhUnitName);
        setRegistrationNumber(entry.registrationNumber ?? '');
        setStartNumber(entry.startNumber?.toString() ?? '');
        setNote(entry.note ?? '');
      } else {
        setFirstName('');
        setLastName('');
        setBirthYear('');
        setGender('M');
        setAgeSubcategory('OSTATNI');
        setSdhUnitName('');
        setRegistrationNumber('');
        setStartNumber('');
        setNote('');
      }
    }
  }, [open, entry]);

  const submit = () => {
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      birthYear: birthYear ? Number(birthYear) : undefined,
      gender,
      ageSubcategory,
      sdhUnitName: sdhUnitName.trim(),
      registrationNumber: registrationNumber.trim() || undefined,
      startNumber: startNumber ? Number(startNumber) : undefined,
      note: note.trim() || undefined,
    });
  };

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    sdhUnitName.trim().length > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{entry ? 'Upravit jednotlivce' : 'Nový jednotlivec'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Jméno"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Příjmení"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ročník"
              type="number"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              fullWidth
            />
            <TextField
              label="Pohlaví"
              select
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender)}
              fullWidth
            >
              <MenuItem value="M">Muž</MenuItem>
              <MenuItem value="F">Žena</MenuItem>
            </TextField>
          </Box>
          <TextField
            label="Věková kategorie"
            select
            value={ageSubcategory}
            onChange={(e) => setAgeSubcategory(e.target.value as AgeSubcategory)}
            fullWidth
          >
            {AGE_SUBCATEGORY_ORDER.map((c) => (
              <MenuItem key={c} value={c}>
                {AGE_SUBCATEGORY_LABELS[c]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="SDH"
            value={sdhUnitName}
            onChange={(e) => setSdhUnitName(e.target.value)}
            placeholder="např. SDH Opava-Suché Lazce"
            required
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Evidenční číslo"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              fullWidth
            />
            <TextField
              label="Startovní číslo"
              type="number"
              value={startNumber}
              onChange={(e) => setStartNumber(e.target.value)}
              helperText="Volitelné — lze vylosovat hromadně"
              fullWidth
            />
          </Box>
          <TextField
            label="Poznámka"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Zrušit
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit || loading}>
          {entry ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
